import { Icon, Tabs, useMediaMatch } from '@dittolive/anvil'
import { CaretLeftIcon } from '@phosphor-icons/react'
import { useAppToDatabaseRename } from 'components/hooks'
import { RawLink } from 'components/link'
import paths from 'paths'
import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppManagementContext } from 'scenes/appManagement/context'
import { useAppURLParams } from 'scenes/appManagement/hooks'

export default function AppSettingsNavigation() {
  const { pathname } = useLocation()
  const slugs = pathname.split('/').filter(Boolean)
  const lastSlug = slugs[slugs.length - 1]
  const navigate = useNavigate()
  const params = useAppURLParams()
  const isMobile = useMediaMatch('(max-width: 768px)')
  const { viewerCanAccessAdminSettings, viewerCanAccessMongo } =
    useAppManagementContext()
  const { isRenamed } = useAppToDatabaseRename()

  const items = useMemo(
    () => [
      {
        key: 'general',
        text: 'General',
        to: paths.appManagement({
          appSlug: params.appSlug,
          organizationSlug: params.organizationSlug || '',
          tenantId: params.userId,
          area: 'settings/general',
          isRenamed,
        }),
      },
      ...(viewerCanAccessMongo
        ? [
            {
              key: 'mongo',
              text: 'MongoDB Connector',
              to: paths.appManagement({
                appSlug: params.appSlug,
                organizationSlug: params.organizationSlug || '',
                tenantId: params.userId,
                area: 'settings/mongo',
                isRenamed,
              }),
            },
          ]
        : []),
      ...(viewerCanAccessAdminSettings
        ? [
            {
              key: 'admin',
              text: 'Administrator',
              to: paths.appManagement({
                appSlug: params.appSlug,
                organizationSlug: params.organizationSlug || '',
                tenantId: params.userId,
                area: 'settings/admin',
                isRenamed,
              }),
            },
          ]
        : []),
    ],
    [
      isRenamed,
      params.appSlug,
      params.organizationSlug,
      params.userId,
      viewerCanAccessAdminSettings,
      viewerCanAccessMongo,
    ],
  )

  const selected = items.find((item) => item.key === lastSlug)

  useEffect(() => {
    if (!selected && items.length > 0 && !isMobile) {
      navigate(items[0].to, { replace: true })
    }
  }, [selected, navigate, items, isMobile])

  const handleChange = useCallback(
    (id: string) => {
      const navigateToItem = items.find(({ key }) => key === id)
      if (navigateToItem?.to) {
        navigate(navigateToItem.to)
      }
    },
    [navigate, items],
  )

  if (!selected) {
    return null
  }

  if (isMobile) {
    return (
      <div className="col-span-12 pt-1">
        <RawLink
          to={paths.appManagement({
            appSlug: params.appSlug,
            organizationSlug: params.organizationSlug || '',
            tenantId: params.userId,
            area: 'settings',
            isRenamed,
          })}
          className="focus-visible:outline-focus-outline text-foreground-normal flex h-12 w-full items-center text-base outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
        >
          <Icon svg={<CaretLeftIcon />} className="mr-2" />
          {selected.text}
        </RawLink>
      </div>
    )
  }

  return (
    <div className="col-span-5 h-full shrink-0">
      <Tabs
        orientation="vertical"
        value={selected.key}
        onValueChange={handleChange}
      >
        <Tabs.List variant="pill" className="flex-col items-stretch">
          {items.map(({ text, key }) => (
            <Tabs.Trigger key={key} value={key}>
              {text}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs>
    </div>
  )
}
